import React from "react";
import {
  Activity,
  AlertOctagon,
  CheckCircle2,
  Clock3,
  FileCode2,
  Gauge,
  ShieldAlert,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { formatDuration, formatScore } from "../lib/utils";
import { Badge, IssueIcon, ScorePill, SectionHeader, StatusMark } from "./Primitives";
import { getTaskSummary, useLocale, localizeIssue } from "../lib/i18n";

const RunCard = ({ run }) => {
  const { t } = useLocale();
  const statusLabel = run.agentStatus === "completed"
    ? t("common.statusCompleted")
    : run.agentStatus === "failed"
      ? t("common.statusFailed")
      : t("common.statusUnknown");
  return (
  <article className="run-card">
    <div className="run-card-top">
      <div className="model-icon">{run.modelLabel.startsWith("GPT") ? "G" : "O"}</div>
      <div>
        <strong>{run.modelLabel}</strong>
        <small>{run.solver} · {formatDuration(run.durationSec)}</small>
      </div>
      <StatusMark
        passed={run.status === "completed" && run.agentStatus === "completed" && !run.verifierError}
        label={run.agentStatus === "completed" ? statusLabel : t("common.agent", { status: statusLabel })}
      />
    </div>
    <div className="score-grid">
      <ScorePill value={run.finalReward} label={t("common.final")} />
      <ScorePill value={run.gradedScore} label={t("common.graded")} />
      <ScorePill value={run.effectiveScore} label={t("common.effective")} />
    </div>
    <div className="run-flags">
      <span className={run.gating === 1 ? "ok" : "bad"}>{t("common.gate", { value: run.gating ?? "—" })}</span>
      {run.gateClipped ? <span className="warn">{t("common.gateClipped")}</span> : null}
      {run.taskTreeStable === false ? <span className="bad">{t("common.hashChanged")}</span> : null}
      {run.failedGates.length ? <span className="bad">{t("common.gatesFailed", { count: run.failedGates.length })}</span> : null}
    </div>
  </article>
  );
};

const ControlCard = ({ label, control, target }) => {
  const { t } = useLocale();
  return (
  <div className="control-card">
    <div>
      <span>{label}</span>
      <strong>{formatScore(control?.reward)}</strong>
    </div>
    <StatusMark
      passed={
        Boolean(control) &&
        !control.verifierError &&
        (label === t("common.oracle") ? control.reward >= target : control.reward <= target)
      }
      label={control ? (control.verifierError ? t("common.verifierError") : t("common.measured")) : t("common.missing")}
    />
  </div>
  );
};

export default function Overview({ sample, onJump }) {
  const { locale, t } = useLocale();
  const { analysis } = sample;
  const highRuns = sample.runs.filter((run) => run.effectiveScore >= 0.5);
  return (
    <div className="page-stack">
      <section>
        <SectionHeader
          eyebrow={t("overview.acceptanceEyebrow")}
          title={t("overview.acceptance")}
          aside={
            <div className={`verdict ${analysis.passed ? "verdict-pass" : "verdict-fail"}`}>
              {analysis.passed ? <CheckCircle2 size={16} /> : <AlertOctagon size={16} />}
              {analysis.passed ? t("overview.staticPass") : t("overview.hardErrors", { count: analysis.errors })}
            </div>
          }
        />
        <div className="hero-grid">
          <div className="hero-main panel">
            <div className="hero-orbit"><span /><span /><span /></div>
            <div className="hero-copy">
              <Badge tone={sample.difficulty === "L5" ? "violet" : "neutral"}>{sample.difficulty}</Badge>
              <h3>{sample.title}</h3>
              <p>{getTaskSummary(sample.id, locale, sample.capabilities || sample.sourceNote || t("overview.waitingCapabilities"))}</p>
              <div className="breadcrumb-line">
                {sample.category.map((item) => <span key={item}>{item}</span>)}
              </div>
            </div>
          </div>
          <div className="metric-tile panel">
            <Gauge size={18} />
            <span>{t("overview.rubrics")}</span>
            <strong>{analysis.metrics.rubricCount}</strong>
            <small>{t("overview.rubricsMeta", { gates: analysis.metrics.gateCount, programmatic: analysis.metrics.programmaticCount })}</small>
          </div>
          <div className="metric-tile panel">
            <FileCode2 size={18} />
            <span>{t("overview.programmaticShare")}</span>
            <strong>{(analysis.metrics.programmaticShare * 100).toFixed(1)}%</strong>
            <small>{t("overview.programmaticTarget")}</small>
          </div>
          <div className="metric-tile panel">
            <Sparkles size={18} />
            <span>{t("overview.anchorWeight")}</span>
            <strong>{(analysis.metrics.anchorRatio * 100).toFixed(1)}%</strong>
            <small>{t("overview.anchorTarget")}</small>
          </div>
        </div>
      </section>

      <section>
        <SectionHeader
          eyebrow={t("overview.rolloutsEyebrow")}
          title={t("overview.rollouts")}
          aside={<span className="muted">{t("overview.scoreHint")}</span>}
        />
        <div className="run-grid">
          {sample.runs.map((run) => <RunCard run={run} key={run.id} />)}
          {!sample.runs.length ? <div className="panel missing-panel">{t("overview.noRollouts")}</div> : null}
        </div>
        {highRuns.length ? (
          <div className="inline-alert error-alert">
            <TriangleAlert size={16} />
            <span>{t("overview.tooEasy", { scores: highRuns.map((run) => `${run.modelLabel}=${run.effectiveScore.toFixed(3)}`).join(locale === "en" ? ", " : "，") })}</span>
          </div>
        ) : null}
      </section>

      <section>
        <SectionHeader eyebrow={t("overview.controlsEyebrow")} title={t("overview.controls")} />
        <div className="control-grid">
          <ControlCard label={t("common.oracle")} control={sample.controls.oracle} target={0.7} />
          <ControlCard label={t("common.nop")} control={sample.controls.nop} target={0.1} />
        </div>
      </section>

      <section>
        <SectionHeader
          eyebrow={t("overview.auditEyebrow")}
          title={t("overview.keyIssues")}
          aside={<button className="text-button" onClick={() => onJump("requirements")}>{t("overview.viewAll")}</button>}
        />
        <div className="issue-preview panel">
          {analysis.issues.slice(0, 8).map((item, index) => (
            <div className={`issue-row issue-${item.severity}`} key={`${item.code}-${index}`}>
              <IssueIcon severity={item.severity} />
              <div><strong>{item.code}</strong><span>{localizeIssue(item, locale, t)}</span></div>
              <code>{item.path || "—"}</code>
            </div>
          ))}
          {!analysis.issues.length ? (
            <div className="all-clear"><CheckCircle2 size={21} />{t("overview.noViolations")}</div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
